import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Search, Route, User, LogIn, Map } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';

const MobileNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const navRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isMobile) return;
    const updateNavHeight = () => {
      const h = navRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty('--mobile-nav-height', `${h}px`);
    };
    updateNavHeight();
    window.addEventListener('resize', updateNavHeight);
    return () => window.removeEventListener('resize', updateNavHeight);
  }, [isMobile]);

  // Only show on mobile devices
  if (!isMobile) return null;

  const navItems = [
    {
      id: 'explore',
      label: 'Explorer',
      icon: <Search className="w-5 h-5" />,
      path: '/',
      isActive: location.pathname === '/'
    },
    {
      id: 'map',
      label: 'Visites',
      icon: <Map className="w-5 h-5" />,
      path: '/map',
      isActive: location.pathname === '/map'
    },
    {
      id: 'tours',
      label: 'Tours',
      icon: <Route className="w-5 h-5" />,
      path: '/tours',
      isActive: location.pathname === '/tours'
    },
    {
      id: 'profile',
      label: user ? 'Profil' : 'Connexion',
      icon: user ? <User className="w-5 h-5" /> : <LogIn className="w-5 h-5" />,
      path: user ? '/profile' : '/auth',
      isActive: user ? location.pathname === '/profile' : location.pathname === '/auth'
    }
  ];

  return (
    <div ref={navRef} className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-t border-border">
      <div className="flex items-center justify-around py-2 px-4 safe-area-bottom">
        {navItems.map((item) => (
          <Button
            key={item.id}
            variant="ghost"
            size="sm"
            onClick={() => navigate(item.path)}
            className={`flex flex-col items-center gap-1 h-auto py-2 px-3 min-w-0 transition-all duration-200 ${
              item.isActive 
                ? 'text-primary bg-primary/10' 
                : 'text-foreground/70 hover:text-foreground'
            }`}
            aria-label={item.label}
            aria-current={item.isActive ? 'page' : undefined}
          >
            <div className={`transition-transform duration-200 ${
              item.isActive ? 'scale-110' : 'hover:scale-105'
            }`}>
              {item.icon}
            </div>
            <span className="text-xs font-semibold truncate max-w-[60px]">
              {item.label}
            </span>
          </Button>
        ))}
      </div>
    </div>
  );
};

export default MobileNavigation;